import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userApi } from '../../services/admin/adminApi';
import { toast } from 'sonner';
import type { UserUpdateRequest } from '../../types/admin';

/**
 * React Query hook for fetching all users
 * @returns Query result with user list
 */
export function useUsers() {
  return useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => userApi.list(),
    select: (response) => response.data || [],
  });
}

/**
 * React Query hook for fetching single user with details
 * Includes related group memberships
 * @param id - User ID
 * @returns Query result with user details
 */
export function useUser(id: number) {
  return useQuery({
    queryKey: ['admin', 'users', id],
    queryFn: () => userApi.get(id),
    select: (response) => response.data,
    enabled: !!id,
  });
}

/**
 * React Query mutation hook for updating user
 * Invalidates user list on success
 * @returns Mutation result for user update
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UserUpdateRequest }) =>
      userApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      toast.success('User updated successfully');
    },
    onError: (error: any) => {
      console.error('Failed to update user:', error);
      toast.error(error?.response?.data?.error?.message || error.message || 'Failed to update user');
    },
  });
}

/**
 * React Query mutation hook for bulk deleting users
 * Invalidates user list on success
 * @returns Mutation result for user deletion
 */
export function useDeleteUsers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userIds: number[]) => userApi.delete(userIds),
    onSuccess: (_, userIds) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      toast.success(`Successfully deleted ${userIds.length} ${userIds.length === 1 ? 'user' : 'users'}`);
    },
    onError: (error: any) => {
      console.error('Failed to delete users:', error);
      toast.error(error?.response?.data?.error?.message || error.message || 'Failed to delete users');
    },
  });
}

/**
 * React Query mutation for minting a password-recovery link.
 * Deliberately does not cache the result: the link is a credential and should
 * live no longer than the admin needs to copy it.
 */
export function useGenerateResetLink() {
  return useMutation({
    mutationFn: (id: number) => userApi.resetLink(id),
    onError: (error: any) => {
      toast.error(error?.error?.message || error?.message || 'Failed to generate reset link');
    },
  });
}

/**
 * React Query mutation for setting a user's password directly.
 * Pass no password to have the server generate a temporary one.
 *
 * Like the reset-link mutation this caches nothing: the response may carry a
 * live credential and should live no longer than the admin needs to copy it.
 */
export function useSetUserPassword() {
  return useMutation({
    mutationFn: ({ id, password }: { id: number; password?: string }) => userApi.setPassword(id, password),
    onError: (error: any) => {
      toast.error(error?.error?.message || error?.message || 'Failed to set password');
    },
  });
}

/**
 * React Query mutation for saving a user's group access on its own.
 *
 * Separate from useUpdateUser because the groups tab owns its own save: it
 * must not carry a stale copy of the profile fields along with it.
 */
export function useUpdateUserGroups() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, groupIds }: { id: number; groupIds: number[] }) => userApi.updateGroups(id, groupIds),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', id] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      toast.success('Group access saved');
    },
    onError: (error: any) => {
      toast.error(error?.error?.message || error?.message || 'Failed to save group access');
    },
  });
}
